import { forwardRef } from 'react'

import {
  CopyPlusIcon,
  DropdownMenu,
  IconButton,
  PencilIcon,
} from '@buffer-mono/popcorn'

import { DraftNotesEntryPoint } from '~publish/components/ContentItemNotes'
import { PostCardContent } from '~publish/components/PostCardContent'
import { readFragment } from '~publish/graphql'
import { draftActionLabels } from '~publish/helpers/posts/draftActionLabels'
import { isOfType } from '~publish/helpers/typeGuards'
import { useContentItemNotesEnabled } from '~publish/hooks/notes/useContentItemNotesEnabled'
import { useDateTimeFormatter } from '~publish/hooks/useDateTimeFormatter'
import { usePostComposer } from '~publish/hooks/usePostComposer'

import { MoreActionsDropdown } from '../PostCard/PostCardActions/MoreActionsDropdown'
import { PostMediaAsset_Asset } from '../PostCard/PostMedia/PostMediaAsset'
import { ContentItemCardAuthor } from './ContentItemCardAuthor'
import { ContentItemMedia } from './ContentItemMedia'
import { CopyDraftLinkMenuItem } from './CopyDraftLinkMenuItem'
import { DeleteContentItemMenuItem } from './DeleteContentItemMenuItem'
import { useContentItemDraftActions } from './useContentItemDraftActions'
import type { ContentItemDraft } from './usePaginatedContentItems'

import { ContentItemCardView } from './ContentItemCardView'
import styles from './ContentItemCard.module.css'

const LABEL_MAX_LENGTH = 60

// Per-draft accessible name so a screen-reader user listing Edit buttons in the
// drafts feed can tell which draft each one targets; the visible tooltip stays
// the plain "Edit".
export const getDraftEditLabel = (text: string): string => {
  const snippet = labelSnippet(text)
  return snippet ? `Edit draft "${snippet}"` : 'Edit draft'
}

export const getDraftPreviewLabel = (text: string): string => {
  const snippet = labelSnippet(text)
  return snippet ? `Preview draft "${snippet}"` : 'Preview draft'
}

const labelSnippet = (text: string): string => {
  const trimmed = text.trim()
  return trimmed.length > LABEL_MAX_LENGTH
    ? `${trimmed.slice(0, LABEL_MAX_LENGTH)}…`
    : trimmed
}

type ContentItemCardProps = {
  contentItem: ContentItemDraft
  className?: string
}

/**
 * Card for a channel-less draft. Mirrors PostCard's body (text + media) but has
 * no channel header — a draft is not yet tied to a channel. Like PostCard, the
 * card body is not itself a control: Edit (an explicit action) opens the draft
 * in the composer, and the actions menu holds Duplicate and Delete. The Notes
 * icon opens the draft's Notes in a popover while ContentItem Notes are on;
 * for an account that can edit the draft, Expand opens it in the composer
 * with the Notes panel.
 */
export const ContentItemCard = forwardRef<HTMLElement, ContentItemCardProps>(
  ({ contentItem, className }, ref) => {
    const { body } = contentItem
    const { triggerAttributes } = usePostComposer()
    const { edit, openNotes, duplicate } =
      useContentItemDraftActions(contentItem)
    const dateTimeFormatter = useDateTimeFormatter()
    const notesEnabled = useContentItemNotesEnabled()
    const canEdit = contentItem.allowedActions.includes('updateContentItem')

    // The list only ever passes DraftContent-bodied items; guard so the card is
    // safe to render for any ContentItem without leaking a channel-post body.
    if (!isOfType(body, 'DraftContent')) {
      return null
    }

    const { editLabel, moreActionsLabel } = draftActionLabels({
      text: body.text,
      title: contentItem.title,
      createdAtLabel: dateTimeFormatter(contentItem.createdAt, {
        includeTimeZone: false,
      }),
      assets: readFragment(PostMediaAsset_Asset, body.assets),
    })

    return (
      <ContentItemCardView
        ref={ref}
        className={className}
        data-testid="content-item-card"
        data-content-item-id={contentItem.id}
        content={<PostCardContent text={body.text} annotations={[]} />}
        media={<ContentItemMedia assets={body.assets} />}
        notes={
          notesEnabled && (
            <DraftNotesEntryPoint
              contentItemId={contentItem.id}
              text={body.text}
              onExpand={canEdit ? openNotes : undefined}
              variant="secondary"
              size="medium"
              className={styles.notesTrigger}
            />
          )
        }
        meta={
          <ContentItemCardAuthor
            createdAt={contentItem.createdAt}
            author={contentItem.author}
          />
        }
        actions={
          <>
            {canEdit && (
              <IconButton
                {...triggerAttributes}
                variant="secondary"
                label={editLabel}
                tooltip="Edit"
                onClick={edit}
              >
                <PencilIcon />
              </IconButton>
            )}
            <MoreActionsDropdown label={moreActionsLabel}>
              <MoreActionsDropdown.Group>
                <CopyDraftLinkMenuItem contentItemId={contentItem.id} />
              </MoreActionsDropdown.Group>
              <MoreActionsDropdown.Group>
                {/* Duplicate writes nothing: it seeds a fresh composer, and
                    Save creates a new draft under its own authorization. */}
                <DropdownMenu.Item onSelect={duplicate}>
                  <CopyPlusIcon />
                  Duplicate
                </DropdownMenu.Item>
              </MoreActionsDropdown.Group>
              <MoreActionsDropdown.Group>
                <DeleteContentItemMenuItem
                  contentItemId={contentItem.id}
                  allowedActions={contentItem.allowedActions}
                />
              </MoreActionsDropdown.Group>
            </MoreActionsDropdown>
          </>
        }
      />
    )
  },
)
ContentItemCard.displayName = 'ContentItemCard'
